/**
 * Auth storage helpers.
 * Both client portal and DigiX staff sessions use sessionStorage
 * (cleared when the browser tab/window closes).
 */

const AUTH_KEYS = [
  "access_token",
  "refresh_token",
  "user",
  "client",
  "account_type",
  "scope",
  "tenant",
  "tenants",
  "is_authenticated",
  "user_role_permissions",
  "portal_notice",
];

function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (_) {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch (_) {}
}

function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch (_) {}
}

function clearKeys(storage) {
  AUTH_KEYS.forEach((key) => safeRemove(storage, key));
}

export function getAccountType() {
  return safeGet(sessionStorage, "account_type") || safeGet(localStorage, "account_type");
}

export function isClientPortalSession() {
  return getAccountType() === "client";
}

export function getAuthItem(key) {
  const fromSession = safeGet(sessionStorage, key);
  if (fromSession != null) return fromSession;
  return safeGet(localStorage, key);
}

export function setAuthItem(key, value) {
  if (value == null) {
    safeRemove(sessionStorage, key);
    safeRemove(localStorage, key);
    return;
  }
  safeSet(sessionStorage, key, value);
  safeRemove(localStorage, key);
}

export function removeAuthItem(key) {
  safeRemove(sessionStorage, key);
  safeRemove(localStorage, key);
}

function persistCommon({ access_token, refresh_token, scope, tenant, tenants }, accountType) {
  clearKeys(localStorage);
  clearKeys(sessionStorage);
  if (access_token) safeSet(sessionStorage, "access_token", access_token);
  if (refresh_token) safeSet(sessionStorage, "refresh_token", refresh_token);
  if (scope) safeSet(sessionStorage, "scope", scope);
  if (tenant) safeSet(sessionStorage, "tenant", JSON.stringify(tenant));
  if (Array.isArray(tenants)) safeSet(sessionStorage, "tenants", JSON.stringify(tenants));
  safeSet(sessionStorage, "account_type", accountType);
  safeSet(sessionStorage, "is_authenticated", "true");
}

export function persistClientPortalSession(data) {
  persistCommon(data, "client");
  if (data.client) safeSet(sessionStorage, "client", JSON.stringify(data.client));
}

export function persistStaffSession(data) {
  persistCommon(data, "staff");
  if (data.user) safeSet(sessionStorage, "user", JSON.stringify(data.user));
}

/** platform | owner | employee (null for sessions created before the unified login). */
export function getSessionScope() {
  return getAuthItem("scope");
}

function parseJson(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
}

/** Business this session acts in ({ Tenant_Id, Company_Name, ... }). */
export function getSessionTenant() {
  return parseJson(getAuthItem("tenant"), null);
}

/** Businesses the signed-in person can switch between. */
export function getSessionTenants() {
  const list = parseJson(getAuthItem("tenants"), []);
  return Array.isArray(list) ? list : [];
}

/** Store a sign-in / switch-tenant response for whichever app it belongs to. */
export function persistSignedInSession(data) {
  if (data?.account_type === "client") {
    persistClientPortalSession(data);
    return;
  }
  persistStaffSession(data);
  const perms = data?.user?.roles?.[0]?.permissions;
  if (Array.isArray(perms) && perms.length > 0) {
    safeSet(sessionStorage, "user_role_permissions", JSON.stringify(perms));
  }
}

export function updateClientPortalProfile(client) {
  if (!client) return;
  safeSet(sessionStorage, "client", JSON.stringify(client));
  safeRemove(localStorage, "client");
}

export function clearAuthStorageKeys() {
  clearKeys(localStorage);
  clearKeys(sessionStorage);
}

/**
 * Move any leftover auth keys from localStorage → sessionStorage once,
 * then clear localStorage auth so a later tab close cannot resurrect the session.
 */
export function migrateLegacyAuthToSession() {
  try {
    const sessionType = safeGet(sessionStorage, "account_type");
    if (sessionType === "client" || sessionType === "staff") {
      clearKeys(localStorage);
      // Sessions from before the unified login carry tokens the API now rejects.
      if (!safeGet(sessionStorage, "scope")) clearKeys(sessionStorage);
      return;
    }

    const localType = safeGet(localStorage, "account_type");
    if (localType !== "client" && localType !== "staff") {
      // Orphan token cleanup
      if (safeGet(localStorage, "access_token") || safeGet(localStorage, "refresh_token")) {
        clearKeys(localStorage);
      }
      return;
    }

    if (safeGet(localStorage, "scope")) {
      AUTH_KEYS.forEach((key) => {
        const value = safeGet(localStorage, key);
        if (value != null) safeSet(sessionStorage, key, value);
      });
    }
    clearKeys(localStorage);
  } catch (_) {}
}

/** @deprecated Use migrateLegacyAuthToSession */
export function migrateLegacyClientAuthToSession() {
  migrateLegacyAuthToSession();
}
